import { useState, useEffect, useCallback } from "react";
import { trackEvent } from "../lib/clarityEvents";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const GAMES_KEY = "nigunim:gamesPlayed";
const DISMISSED_KEY = "nigunim:installDismissed";
const GAMES_THRESHOLD = 3;

export function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(ios);

    const dismissed = localStorage.getItem(DISMISSED_KEY) === "true";
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches;
    if (dismissed || isStandalone) return;

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const checkShouldShow = useCallback(() => {
    const dismissed = localStorage.getItem(DISMISSED_KEY) === "true";
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches;
    if (dismissed || isStandalone) return;

    const games = parseInt(localStorage.getItem(GAMES_KEY) || "0", 10);
    if (games >= GAMES_THRESHOLD && (deferredPrompt || isIOS)) {
      setShowBanner(true);
    }
  }, [deferredPrompt, isIOS]);

  const install = useCallback(async () => {
    if (deferredPrompt) {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        trackEvent("install_pwa");
        setShowBanner(false);
      }
      setDeferredPrompt(null);
    }
  }, [deferredPrompt]);

  const dismiss = useCallback(() => {
    localStorage.setItem(DISMISSED_KEY, "true");
    setShowBanner(false);
  }, []);

  const autoHide = useCallback(() => {
    setShowBanner(false);
  }, []);

  return { showBanner, isIOS, install, dismiss, autoHide, checkShouldShow, deferredPrompt };
}

export function incrementGamesPlayed() {
  const current = parseInt(localStorage.getItem(GAMES_KEY) || "0", 10);
  localStorage.setItem(GAMES_KEY, String(current + 1));
}
